import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { ScaledViewport } from './ScaledViewport';
import { CaprelsaSnak } from './snak/CaprelsaSnak';
type SnakPlayerOverlayProps = {
  open: boolean;
  onClose: () => void;
};

/** Full-screen, focused player for the Caprelsa Snak — always starts from the first scene. */
export function SnakPlayerOverlay({
  open,
  onClose
}: SnakPlayerOverlayProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return createPortal(<AnimatePresence data-id="element-627" data-mp="1s6wwnho9ivt4">
      {open && <motion.div role="dialog" aria-modal="true" aria-label="Play the Caprelsa Snak" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} onClick={onClose} className="fixed inset-0 z-[95] flex flex-col items-center justify-center bg-[var(--fg)]/85 p-4 backdrop-blur-sm hide-on-print" data-id="element-628" data-mp="1s5vkf9vig39t">
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close the Snak" className="absolute right-4 top-4 flex h-9 items-center gap-1.5 rounded-full bg-white px-3.5 text-xs font-semibold text-[var(--fg)] shadow-sm transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--fg)]" data-id="element-629" data-mp="1k276c2bmhu2o">
            <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-630" data-mp="2qkx3uog5rabm" />
            Close
          </button>

          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.98
      }} transition={{
        duration: 0.4,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={e => e.stopPropagation()} data-snak-device className="h-[min(860px,calc(100vh-5rem))] w-full max-w-[460px]" data-id="element-631" data-mp="2viymx9dl39n3">
            <ScaledViewport baseWidth={390} baseHeight={760} bezel={10} radius={0.13} className="h-full w-full" deviceClassName="bg-ink shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)]" data-id="element-632" data-mp="2f752shcccgpc">
              <div className="flex h-full flex-col bg-white" data-id="element-633" data-mp="331etudu5z4zt">
                <CaprelsaSnak data-id="element-634" data-mp="2y4oiu4x7uzl9" />
              </div>
            </ScaledViewport>
          </motion.div>

          <p className="mt-3 font-mono text-[0.62rem] uppercase tracking-[0.16em] text-white/60" data-id="element-635" data-mp="3vjk6zzd4yhd5">Press Esc or click outside to close</p>
        </motion.div>}
    </AnimatePresence>, document.body);
}